/* Which person is signed in to Jackdesk, shared across the apps.
 *
 * A **user id in a cookie on the parent domain, never a token**. The id says
 * "this is who is signed in here"; it grants nothing, so a cookie readable by
 * every app on the domain is not a credential lying around. The token stays in
 * each app's own storage, where another origin cannot reach it.
 *
 * Two rules the customers-ui original established and this keeps:
 *
 *  - it is published only on an **explicit sign-in**, never on a page load, or
 *    an app that merely opened would claim the identity;
 *  - **any doubt means "no opinion", never a sign-out.** Cookies blocked,
 *    a parse failure, a different domain: all of them mean the apps stay
 *    independent, because signing somebody out because a cookie was unreadable
 *    is a far worse failure than not noticing they switched accounts.
 */
const COOKIE = 'jd_identity'

function cookieDomain(): string | null {
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return null
  return /(^|\.)jackdesk\.com$/i.test(host) ? '.jackdesk.com' : null
}

export function readIdentity(): string | null {
  try {
    const raw = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE + '=([^;]*)'))?.[1]
    if (raw == null) return null
    return decodeURIComponent(raw).trim() || null
  } catch {
    return null
  }
}

export function publishIdentity(userId: number | string | null | undefined): void {
  if (userId == null || userId === '') return
  try {
    const domain = cookieDomain()
    document.cookie = [
      `${COOKIE}=${encodeURIComponent(String(userId))}`, 'path=/',
      domain ? `domain=${domain}` : '', 'max-age=2592000',
      window.location.protocol === 'https:' ? 'secure' : '', 'samesite=Lax',
    ].filter(Boolean).join('; ')
  } catch { /* cookies blocked: the apps stay independent */ }
}

export function clearIdentity(): void {
  try {
    const domain = cookieDomain()
    document.cookie = [`${COOKIE}=`, 'path=/', domain ? `domain=${domain}` : '', 'max-age=0']
      .filter(Boolean).join('; ')
  } catch { /* nothing to do */ }
}

/** Somebody signed in as a different person in another Jackdesk app. */
export function isStaleIdentity(localUserId: number | string | null | undefined): boolean {
  if (localUserId == null || localUserId === '') return false
  const shared = readIdentity()
  return !!shared && String(localUserId) !== shared
}
